import { useState, useRef, useEffect } from 'react';
import { cn } from '@/utils/cn.ts';
import { resolveImagePath } from '@/utils/imageResolver.ts';
import type { StudentMilestonesSection } from '@/data/types.ts';

interface StudentMilestonesProps {
  data: StudentMilestonesSection;
}

export function StudentMilestones({ data }: StudentMilestonesProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const autoPlayRef = useRef<number>();
  const carouselRef = useRef<HTMLDivElement>(null);

  const { title, description, students } = data;

  // Auto-play functionality
  useEffect(() => {
    if (isAutoPlay && students.length > 1) {
      autoPlayRef.current = window.setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % students.length);
      }, 5000);
    } else {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isAutoPlay, students.length]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlay(false); // Pause auto-play when user interacts
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % students.length);
    setIsAutoPlay(false);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + students.length) % students.length);
    setIsAutoPlay(false);
  };

  const currentStudent = students[currentIndex];

  return (
    <section className="bg-white py-16">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {description}
          </p>
        </div>

        <div className="relative max-w-4xl mx-auto">
          {/* Main carousel content */}
          <div ref={carouselRef} className="relative overflow-hidden rounded-lg bg-gray-50">
            <div className="grid md:grid-cols-2 items-center min-h-[400px]">
              {/* Student image */}
              <div className="relative">
                <img
                  src={resolveImagePath(currentStudent.image)}
                  alt={`${currentStudent.name} - ${currentStudent.achievement}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent"></div>
              </div>

              {/* Student info */}
              <div className="p-8 md:p-12">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  {currentStudent.name}
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p className="text-lg">
                    <span className="font-semibold">{currentStudent.achievement}</span> on {currentStudent.date}
                  </p>
                  <p>
                    <span className="text-gray-500">Instructor:</span> {currentStudent.instructor}
                  </p>
                </div>
                <a
                  href={currentStudent.url}
                  className="inline-block mt-6 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-300"
                >
                  Read Full Story
                </a>
              </div>
            </div>
          </div>

          {/* Navigation arrows */}
          {students.length > 1 && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-3 rounded-full shadow-lg transition-all duration-200 z-10"
                aria-label="Previous student"
              >
                <svg
                  className="w-6 h-6 text-gray-800"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-3 rounded-full shadow-lg transition-all duration-200 z-10"
                aria-label="Next student"
              >
                <svg
                  className="w-6 h-6 text-gray-800"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </>
          )}

          {/* Pagination dots */}
          {students.length > 1 && (
            <div className="flex justify-center space-x-2 mt-8">
              {students.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={cn(
                    'w-3 h-3 rounded-full transition-all duration-200',
                    index === currentIndex
                      ? 'bg-blue-600 scale-125'
                      : 'bg-gray-300 hover:bg-gray-400'
                  )}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Student name list for accessibility and SEO */}
        <div className="mt-12 text-center">
          <div className="inline-flex flex-wrap gap-4 justify-center">
            {students.map((student, index) => (
              <button
                key={student.id}
                onClick={() => goToSlide(index)}
                className={cn(
                  'px-4 py-2 rounded-full text-sm font-medium transition-all duration-200',
                  index === currentIndex
                    ? 'bg-blue-100 text-blue-800 shadow-md'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                )}
              >
                {student.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}